import { useCallback, useState } from 'react';
import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import Dialog, { Next } from '../molecules/Dialog';
import Image from '../atoms/Image';
import SpeechBubble from '../atoms/SpeechBubble';
import Text from '../atoms/Text';
import AutofitText from '../molecules/AutofitText';
import StarRatingComponent from 'react-star-rating-component';
import { NARRATOR } from '../../data/Personas';
import { getImageByKey } from '../../assets/Assets';

export interface Props {
	surveyUrl?: string;
	onNext: (ratingValue?: number) => void;
}

const UnicodeSymbol = styled.span`
	font-family: -apple-system, sans-serif;
`;

const StarRating = styled(StarRatingComponent)`
	font-size: 250%;
	line-height: 1;
`;

const Link = styled.a`
	font-size: 150%;
	color: ${(props) => props.theme.colors.veryDarkBlue};
`;

const ActiveStarIcon = styled.span`
	color: ${(props) => props.theme.colors.yellow};
`;

const InactiveStarIcon = styled.span`
	color: ${(props) => props.theme.colors.gray};
`;

const FeedbackDialog: FunctionComponent<Props> = (
	props: PropsWithChildren<Props>
) => {
	const { children, surveyUrl, onNext } = props;
	const [ratingValue, setRatingValue] = useState<number>(0);
	const { t } = useTranslation();

	const handleNext = useCallback(() => {
		const value =
			ratingValue >= 1 && ratingValue <= 5 ? ratingValue : undefined;
		onNext(value);
	}, [ratingValue, onNext]);

	const renderStarIcon = useCallback(
		(starValue: number, currentValue: number): ReactNode => {
			if (currentValue >= starValue) {
				return (
					<ActiveStarIcon>
						<UnicodeSymbol>&#9733;</UnicodeSymbol>
					</ActiveStarIcon>
				);
			} else {
				return (
					<InactiveStarIcon>
						<UnicodeSymbol>&#9734;</UnicodeSymbol>
					</InactiveStarIcon>
				);
			}
		},
		[]
	);

	return (
		<Dialog>
			<Image
				src={getImageByKey(NARRATOR.images.body.positive)}
				width="38vmin"
				height="55.6vmin"
				position="absolute"
				left="11%"
				bottom="0"
			/>
			<SpeechBubble
				anchor="left_bottom"
				position="absolute"
				left="calc(11% + 38vmin + 2%)"
				right="6%"
				top="8%"
				bottom="28%"
			>
				<AutofitText>
					<Text content={t('feedback.rate')} />
					<StarRating
						name="rating"
						value={ratingValue}
						onStarClick={(value) => setRatingValue(value)}
						renderStarIcon={renderStarIcon}
					/>
					{surveyUrl ? (
						<>
							<Text content={t('feedback.survey')} />
							<Link href={surveyUrl} target="_blank" title={surveyUrl}>
								<Trans>feedback.to_survey</Trans>
							</Link>
						</>
					) : undefined}
				</AutofitText>
			</SpeechBubble>
			<Next onClick={handleNext}>
				<Trans>feedback.next</Trans>
			</Next>
			{children}
		</Dialog>
	);
};

export default FeedbackDialog;
